<script setup lang="ts">
import { EBadge, EButton, EDate, EStatusDot } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import { deepLink, isUnread, severityTone, type NotificationDTO } from '@/modules/notifications/api'
import { useAuthStore } from '@/stores/auth'

import './notifications.css'

/**
 * One notification, in the bell panel and in the inbox alike.
 *
 * Four decisions worth keeping:
 *
 *  - **The title is a link when there is somewhere to go.** CLAUDE.md §6 asks
 *    for emails "with deep links"; the in-app equivalent is the entire point of
 *    the bell. A row with no `link` renders as text rather than as a dead
 *    anchor, because a link that goes nowhere is worse than no link.
 *  - **Unread is stated three ways** — an accent rail, the word "Unread", and
 *    the mark-read button's accessible name. WCAG 1.4.1: never hue alone.
 *  - **Severity is a dot with a real label.** `EStatusDot` always renders its
 *    label for assistive technology, so "Warning" is announced even though the
 *    screen shows a 8px circle.
 *  - **The instant is rendered in the tenant timezone** (§5), through the
 *    signed-in user's profile override when they have one. "Received 09:15" has
 *    to mean 09:15 where the business is, not where the laptop is.
 */

const props = defineProps<{
  item: NotificationDTO
  /** Panel rows drop the body text and the per-row dismiss; the inbox keeps both. */
  compact?: boolean
}>()

const emit = defineEmits<{
  /** The user followed the deep link — the row is read by definition. */
  activate: [item: NotificationDTO]
  markRead: [item: NotificationDTO]
  dismiss: [item: NotificationDTO]
}>()

const { t } = useI18n()
const auth = useAuthStore()

const unread = computed(() => isUnread(props.item))
const target = computed(() => deepLink(props.item))

/**
 * The kind, as a person reads it.
 *
 * The five cases are the closed set `internal/notifications/models.go` declares
 * today. The column carries no CHECK constraint on purpose — later modules add
 * kinds without a migration — so an unknown kind renders its own machine string
 * rather than a made-up label: `ap.invoice.posted` tells the reader something,
 * "Notification" tells them nothing.
 */
const kindLabel = computed(() => {
  switch (props.item.kind) {
    case 'approval.assigned':
      return t('notifications.kinds.approvalAssigned')
    case 'approval.reminded':
      return t('notifications.kinds.approvalReminded')
    case 'approval.escalated':
      return t('notifications.kinds.approvalEscalated')
    case 'mention':
      return t('notifications.kinds.mention')
    case 'system':
      return t('notifications.kinds.system')
    default:
      return props.item.kind
  }
})

/** Mirrors `notif_severity_check`; anything else is neutral and unlabelled. */
const severityLabel = computed(() => {
  switch (props.item.severity) {
    case 'success':
      return t('notifications.severities.success')
    case 'warning':
      return t('notifications.severities.warning')
    case 'danger':
      return t('notifications.severities.danger')
    default:
      return t('notifications.severities.info')
  }
})
</script>

<template>
  <li
    class="notif-item"
    :class="{ 'is-unread': unread }"
    :data-testid="`notif-item-${item.id}`"
    :data-unread="unread ? 'true' : 'false'"
  >
    <EStatusDot class="notif-item__dot" :tone="severityTone(item.severity)" :label="severityLabel" />

    <div class="notif-item__body">
      <div class="notif-item__title">
        <RouterLink
          v-if="target !== ''"
          :to="target"
          :data-testid="`notif-link-${item.id}`"
          @click="emit('activate', item)"
        >
          {{ item.title }}
        </RouterLink>
        <span v-else>{{ item.title }}</span>

        <EBadge v-if="unread" tone="accent" size="sm" :data-testid="`notif-unread-tag-${item.id}`">
          {{ t('notifications.item.unread') }}
        </EBadge>
      </div>

      <p v-if="!compact && item.body !== ''" class="notif-item__text">{{ item.body }}</p>

      <div class="notif-item__meta">
        <span :data-testid="`notif-kind-${item.id}`">{{ kindLabel }}</span>
        <EDate
          :value="item.createdAt"
          preset="dateTime"
          :time-zone="auth.timeZone"
          :locale="auth.locale"
        />
        <span v-if="!compact && target === ''" :data-testid="`notif-nolink-${item.id}`">
          {{ t('notifications.item.noLink') }}
        </span>
      </div>
    </div>

    <div class="notif-item__actions">
      <EButton
        v-if="unread"
        variant="ghost"
        size="sm"
        icon-only
        :aria-label="t('notifications.item.markReadFor', { title: item.title })"
        :data-testid="`notif-mark-read-${item.id}`"
        @click="emit('markRead', item)"
      >
        <template #icon><AppIcon name="check" :size="14" /></template>
      </EButton>

      <EButton
        v-if="!compact"
        variant="ghost"
        size="sm"
        icon-only
        :aria-label="t('notifications.item.dismissFor', { title: item.title })"
        :data-testid="`notif-dismiss-${item.id}`"
        @click="emit('dismiss', item)"
      >
        <template #icon><AppIcon name="close" :size="14" /></template>
      </EButton>
    </div>
  </li>
</template>
